import { InfoDialogComponent } from "@/components/info-dialog/info-dialog.component";
import { PropertyDocViewComponent } from "@/components/property-doc-view/property-doc-view.component";
import { ObjectUpdatedType } from "@/enums/ObjectUpdatedType.enum";
import { AppConstants } from "@/helpers/app.constants";
import { GeoLocationHelper } from "@/helpers/geolocation.helper";
import { PropertyHelper } from "@/helpers/property.helper";
import { UserHelper } from "@/helpers/user.helper";
import { NewMapComponent } from "@/modules/map/components/new-map/new-map.component";
import { AlertService } from "@/services/alert.service";
import { AppConfigService } from "@/services/app-config.service";
import { IntegerValidator } from "@/validators/IntegerValidator";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import {
  MatExpansionModule,
  MatExpansionPanel,
} from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import {
  CertificationType,
  LandRegistry,
  Location,
  PropertiesService,
  PropertyCategory,
  RealProperty,
} from "@eemo/api-client";
import { MdbPopoverModule } from "mdb-angular-ui-kit/popover";
import { Subject, Subscription } from "rxjs";
import { PropertyAttributesViewComponent } from "../../../components/property-attributes-view/property-attributes-view.component";
import { PropertyDocViewComponent as PropertyDocViewComponent_1 } from "../../../components/property-doc-view/property-doc-view.component";
import { InputDecimalValueDirective } from "../../../extensions/inputDecimalValueDirective";
import { InputNullValueDirective } from "../../../extensions/inputNullValueDirective";
import { InputUnitDirective } from "../../../extensions/inputUnitDirective";
import { MapModule } from "../../../modules/map/map.module";

import { InputComponent } from "@/components/ui-forms/input/input.component";
import { PostalCodeValidator } from "@/validators/PostalCodeValidator";
import { DecimalPipe, NgFor, NgIf, NgTemplateOutlet } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { InputTrimDirective } from "../../../extensions/inputTrimDirective";

export type LandRegistryMutable = {
  -readonly [K in keyof LandRegistry]: LandRegistry[K];
};

@Component({
  selector: "app-property-location",
  templateUrl: "./property-location.component.html",
  styleUrls: ["./property-location.component.scss"],
  standalone: true,
  imports: [
    MatExpansionModule,
    FormsModule,
    ReactiveFormsModule,
    InputComponent,
    NgIf,
    MatFormFieldModule,
    MatInputModule,
    InputTrimDirective,
    MatButtonModule,
    MatIconModule,
    InputDecimalValueDirective,
    InputUnitDirective,
    InputNullValueDirective,
    NgFor,
    NgTemplateOutlet,
    PropertyAttributesViewComponent,
    MapModule,
    PropertyDocViewComponent_1,
    MdbPopoverModule,
    DecimalPipe,
  ],
})
export class PropertyLocationComponent implements OnInit, OnDestroy {
  @ViewChild(PropertyDocViewComponent) docComponent: PropertyDocViewComponent;
  @ViewChild(NewMapComponent) newMapComponent: NewMapComponent;
  @ViewChild("mapPanel") mapPanel: MatExpansionPanel;
  @Input() prop: RealProperty;

  readonly DEFAULT_REGISTRY_PREFIX = "000";

  locationForm: UntypedFormGroup;
  addressForm: UntypedFormGroup;
  landRegistryForm: UntypedFormGroup;
  otherLandRegistriesForm: FormGroup;

  category = PropertyCategory.LOCATION;
  locationEdition = false;
  locationCollapsed = true;
  addressEdition = false;
  addressCollapsed = true;
  landRegistryEdition = false;
  landRegistryCollapsed = false;
  canUpdateProperty = false;
  canUpdateAddress = false;
  canUpdateLocation = false;
  canUpdateRegistry = false;

  docUpdateSub = new Subscription();
  updateLocationSub = new Subscription();
  updateAddressSub = new Subscription();
  updateLandRegistrySub = new Subscription();
  findAddressSub = new Subscription();
  findInseeCodeSub = new Subscription();

  selectedRegistry: string[] = [];

  parcelleSelectedSubject$ = new Subject<string>();
  parcelleSelected$ = this.parcelleSelectedSubject$.asObservable();

  constructor(
    private fb: UntypedFormBuilder,
    private propertiesService: PropertiesService,
    private alertService: AlertService,
    private appConfigService: AppConfigService,
    private userHelper: UserHelper,
    private dialog: MatDialog,
    private geolocationHelper: GeoLocationHelper,
    private propHelper: PropertyHelper
  ) {}

  fLoc(field: string): AbstractControl {
    return this.locationForm.get(field);
  }

  fAddr(field: string): AbstractControl {
    return this.addressForm.get(field);
  }

  fReg(field: string): AbstractControl {
    return this.landRegistryForm.get(field);
  }

  get othersRegistries() {
    return this.otherLandRegistriesForm.get("othersRegistries") as FormArray;
  }

  editAddress() {
    this.addressEdition = true;
  }
  closeEditAddress() {
    this.addressEdition = false;
    this.fillFormAddress();
  }
  editLocation() {
    this.locationEdition = true;
    this.reloadMap();
    if (this.prop.location) {
      this.showEditLocationInfo();
    }
  }
  closeEditLocation() {
    this.locationEdition = false;
    this.fillFormLocation();
    this.reloadMap();
  }
  editLandRegistry() {
    this.landRegistryEdition = true;
  }
  closeEditLandRegistry() {
    this.landRegistryEdition = false;
    this.fillFormLandRegistry();
    this.clearFormOthersLandRegistry();
    this.fillFormOthersLandRegistry();
  }

  ngOnInit() {
    this.canUpdateProperty = this.userHelper.canUpdateProperty(this.prop);
    this.canUpdateAddress =
      this.userHelper.isAdmin() ||
      (this.canUpdateProperty && !this.prop.certified);
    this.canUpdateLocation =
      this.userHelper.isAdmin() ||
      (this.canUpdateProperty &&
        (!this.prop.certified ||
          this.prop.certificationType === CertificationType.PROFESSIONAL));
    this.canUpdateRegistry =
      this.userHelper.isAdmin() ||
      (this.canUpdateProperty &&
        (!this.prop.certified ||
          this.prop.certificationType === CertificationType.PROFESSIONAL));

    /*this.locationCollapsed = this.prop.location ? true : false;
    this.addressCollapsed = this.prop.address ? true : false;*/
    this.addressCollapsed = false;
    this.locationCollapsed = false;

    this.locationForm = this.fb.group({
      latitude: [
        "",
        [
          Validators.required,
          Validators.pattern(AppConstants.FORM_PATTERN_DECIMAL),
          Validators.min(-90),
          Validators.max(90),
        ],
      ],
      longitude: [
        "",
        [
          Validators.required,
          Validators.pattern(AppConstants.FORM_PATTERN_DECIMAL),
          Validators.min(-180),
          Validators.max(180),
        ],
      ],
      altitude: [
        null,
        [
          Validators.pattern(AppConstants.FORM_PATTERN_POSITIVE_INTEGER),
          Validators.min(0),
        ],
      ],
    });

    this.addressForm = this.fb.group({
      street: ["", [Validators.required, Validators.maxLength(50)]],
      complement: ["", [Validators.maxLength(50)]],
      postalCode: ["", [Validators.required, PostalCodeValidator]],
      city: ["", [Validators.required, Validators.maxLength(50)]],
      country: ["", Validators.maxLength(50)],
    });

    this.landRegistryForm = this.fb.group({
      plot: [null, [Validators.required, Validators.pattern("^[0-9]{1,4}$")]],
      inseeCode: [
        null,
        [Validators.required, Validators.pattern("^[0-9A-Z]{5}$")],
      ],
      prefix: [
        this.DEFAULT_REGISTRY_PREFIX,
        [Validators.required, Validators.pattern("^[0-9]{3}$")],
      ],
      section: [
        null,
        [Validators.required, Validators.pattern("^[0-9A-Z]{1,2}$")],
      ],
      sheet: [null, Validators.pattern("^[0-9]{1,2}$")],
      surface: [null, [IntegerValidator, Validators.min(1)]],
      urbanArea: [null, [Validators.maxLength(10)]],
    });

    this.otherLandRegistriesForm = this.fb.group({
      othersRegistries: this.fb.array([]),
    });

    this.fillFormAddress();
    this.fillFormLocation();
    this.fillFormLandRegistry();
    this.fillFormOthersLandRegistry();
    if (!this.prop.address) {
      // si adresse non définie, on passe directement en mode edition
      this.addressEdition = true;
    }

    this.docUpdateSub = this.appConfigService.objectUpdated.subscribe(
      (type) => {
        if (type === ObjectUpdatedType.DOC) {
          this.refreshDocList();
        }
      }
    );
  }

  addOthersRegistries(landRegistry: LandRegistry | null) {
    const toBind = this.getOtherRegistryFormInitial(landRegistry);
    this.othersRegistries.push(toBind);
  }

  getOtherRegistryFormInitial(landRegistry: LandRegistry): FormGroup {
    const isUpdate = landRegistry ? true : false;
    return this.fb.group({
      plot: [
        isUpdate ? landRegistry.plot : null,
        [Validators.required, Validators.pattern("^[0-9]{1,4}$")],
      ],
      inseeCode: [
        isUpdate ? landRegistry.inseeCode : this.prop.landRegistry?.inseeCode,
        [Validators.required, Validators.pattern("^[0-9A-Z]{5}$")],
      ],
      prefix: [
        isUpdate ? landRegistry.prefix : this.prop.landRegistry?.prefix,
        [Validators.required, Validators.pattern("^[0-9]{3}$")],
      ],
      section: [
        isUpdate ? landRegistry.section : this.prop.landRegistry?.section,
        [Validators.required, Validators.pattern("^[0-9A-Z]{1,2}$")],
      ],
      sheet: [
        isUpdate ? landRegistry.sheet : this.prop.landRegistry?.sheet,
        [Validators.pattern("^[0-9]{1,2}$")],
      ],
      surface: [
        isUpdate ? landRegistry.surface : null,
        [IntegerValidator, Validators.min(1)],
      ],
    });
  }

  removeOthersRegistries(index: number) {
    this.othersRegistries.removeAt(index);
  }

  ngOnDestroy(): void {
    this.docUpdateSub.unsubscribe();
    this.updateLocationSub.unsubscribe();
    this.updateAddressSub.unsubscribe();
    this.updateLandRegistrySub.unsubscribe();
    this.findAddressSub.unsubscribe();
    this.findInseeCodeSub.unsubscribe();
  }

  fillFormAddress() {
    if (this.prop.address) {
      this.addressForm.patchValue(this.prop.address);
    }
  }

  fillFormLocation() {
    if (this.prop.location) {
      this.locationForm.patchValue(this.prop.location);
    }
  }

  fillFormLandRegistry() {
    if (this.prop.landRegistry) {
      this.landRegistryForm.patchValue(this.prop.landRegistry);
    }
  }

  fillFormOthersLandRegistry() {
    if (this.prop.otherLandRegistries) {
      this.prop.otherLandRegistries.forEach((l) => {
        this.addOthersRegistries(l);
      });
    }
  }

  clearFormOthersLandRegistry() {
    this.othersRegistries.clear();
  }

  onSubmitLocation() {
    if (this.locationForm.valid) {
      this.locationEdition = false;
      this.prop.location = this.locationForm.value;
      this.updateLandRegistrySub = this.propertiesService
        .updateRealProperty(this.prop.uid, this.prop)
        .subscribe((createdId) => {
          this.alertService.success("Modification effectuée avec succès");
          this.reloadMap();
        });
    }
  }

  onSubmitAddress() {
    if (this.addressForm.valid) {
      this.prop.address = this.addressForm.value;
      this.updateAddressSub = this.propertiesService
        .updateRealProperty(this.prop.uid, this.prop)
        .subscribe((createdId) => {
          this.alertService.success("Modification effectuée avec succès");
        });
      this.addressEdition = false;
    }
  }

  onSubmitLandRegistry() {
    this.otherLandRegistriesForm.markAllAsTouched();
    if (this.landRegistryForm.valid && this.otherLandRegistriesForm.valid) {
      this.prop.landRegistry = this.landRegistryForm.value;
      this.prop.otherLandRegistries = this.othersRegistries.value;
      this.updateLandRegistrySub = this.propertiesService
        .updateRealProperty(this.prop.uid, this.prop)
        .subscribe((createdId) => {
          this.alertService.success("Modification effectuée avec succès");
          this.reloadMap();
        });
      this.landRegistryEdition = false;
      // manually add identifiers (until property reload)
      (this.prop.landRegistry as LandRegistryMutable).identifier =
        this.propHelper.getLandRegistryIdentifier(this.prop.landRegistry);
      (this.prop.landRegistry as LandRegistryMutable).identifierShort =
        this.propHelper.getLandRegistryIdentifierShort(this.prop.landRegistry);
      this.prop.otherLandRegistries.forEach((r) => {
        (r as LandRegistryMutable).identifier =
          this.propHelper.getLandRegistryIdentifier(r);
        (r as LandRegistryMutable).identifierShort =
          this.propHelper.getLandRegistryIdentifierShort(r);
      });
    }
  }

  refreshDocList() {
    if (this.docComponent) {
      this.docComponent.loadDocList();
    }
  }

  confirmFindLocation() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(InfoDialogComponent, {
        // width: '25rem',
        data: {
          title: "À savoir",
          content:
            "<p>Les coordonnées de votre bien vont être récupérées automatiquement à partir de l'adresse que vous avez saisie.</p>" +
            "<p>Pour certaines adresses, la localisation sur la carte peut être approximative.</p>" +
            "<p>Vous pourrez ensuite affiner la localisation directement sur la carte (voir la rubrique d'aide pour plus d'informations)</p>",
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.findLocationFromAddress();
        }
      });
    }
  }

  findLocationFromAddress() {
    if (
      this.prop.address &&
      this.prop.address.street &&
      this.prop.address.postalCode &&
      this.prop.address.city
    ) {
      this.findAddressSub = this.geolocationHelper
        .findLocationFromPropertyAddress(this.prop)
        .subscribe({
          next: (data) => {
            if (data[0]) {
              if (this.prop.location) {
                this.prop.location.latitude = data[0].lat;
                this.prop.location.longitude = data[0].lon;
              } else {
                this.prop.location = {
                  latitude: data[0].lat,
                  longitude: data[0].lon,
                };
              }
              this.locationForm.patchValue(this.prop.location);
              this.propertiesService
                .updateRealProperty(this.prop.uid, this.prop)
                .subscribe((createdId) => {
                  this.alertService.success(
                    "Modification effectuée avec succès"
                  );
                });
            } else {
              this.alertService.warning(
                "Aucune correspondance n'a été trouvée. Veuillez vérifier votre adresse."
              );
            }
          },
          error: (err: HttpErrorResponse) => {
            this.alertService.apiBadRequest(
              err,
              "Erreur de l'API OpenStreetMap"
            );
          },
        });
    }
  }

  /*findCodeInseeFromAddress() {
    if (this.prop.address && this.prop.address.postalCode && this.prop.address.city) {
      this.findInseeCodeSub = this.geolocationHelper.findCodeInseeFromPropertyAddress(this.prop).subscribe({
        next: (data) => {
          if (data[0]) {
            if (this.prop.landRegistry) {
              this.prop.landRegistry.inseeCode = data[0].code;
            } else {
              this.prop.landRegistry = { inseeCode: data[0].code };
            }
            this.landRegistryForm.patchValue(this.prop.landRegistry);
            this.propertiesService.updateRealProperty(this.prop.uid, this.prop).subscribe((createdId) => {
              this.alertService.success('Modification effectuée avec succès');
              this.reloadMap();
            });
          } else {
            this.alertService.warning("Aucune correspondance n'a été trouvée. Veuillez vérifier votre adresse.");
          }
        },
        error: (err: HttpErrorResponse) => {
          this.alertService.apiBadRequest(err, "Erreur de l'API INSEE");
        }
      });
    }
  }*/

  getCityInseeCode() {
    return this.prop.landRegistry
      ? this.prop.landRegistry.inseeCode
      : undefined;
  }

  reloadMap() {
    this.newMapComponent.props = [this.prop];
    this.newMapComponent.updateLocationEnabled = this.locationEdition;
    this.newMapComponent.reloadMap();
  }

  mapLocationUpdated(event: Location) {
    this.fLoc("latitude").setValue(event.latitude);
    this.fLoc("longitude").setValue(event.longitude);
  }

  showEditLocationInfo() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(InfoDialogComponent, {
        maxWidth: "30rem",
        data: {
          title: "Modification de la localisation",
          content:
            "<p>Pour modifier la localisation du bien, vous pouvez:</p>" +
            '<p><ul class="info"><li>Déplacer le marqueur directement sur la carte et affiner sa position</li >' +
            "<li>Où saisir les coordonnées, si vous les connaissez, dans les champs <i>Latitude</i> et <i>Longitude</i></li></p>",
        },
      });
    }
  }
}
